import { Actions, createEffect, ofType } from "@ngrx/effects";
import { AdminService } from "../services/admin.service";
import { Injectable } from "@angular/core";
import { RouterNavigatedAction, routerNavigatedAction } from "@ngrx/router-store";
import {filter, forkJoin, map, switchMap} from "rxjs";
import {
  CaseContactSearchTermChanged,
  FetchAllSystemUsers,
  FetchAllSystemUsersSuccess,
  FetchCaseContacts,
  FetchCaseContactsSuccess,
  FetchClinics,
  FetchClinicsSuccess,
  FetchPortalRequests,
  FetchPortalRequestsSuccess,
  FetchProviders,
  FetchProvidersSuccess
} from "./admin.actions";
import { WebResponse } from "@app/shared/interfaces/webResponse.interface";
import { UserStatusInfo } from "../interfaces/userStatusInfo.interface";
import { PaginatedResult } from "@app/shared/interfaces/paginatedResult.interface";
import { RegistrationService } from "@app/features/registration/services/registration.service";
import { CaseContact } from "@app/shared/interfaces/caseContact.interface";
import { Clinic } from "@app/shared/interfaces/clinic.interface";
import { Provider } from "@app/shared/interfaces/provider.interface";

import {UserPageRequest} from "@app/features/adminDashboard/interfaces/userPageRequest.interface";

@Injectable()
export class AdminEffects {
  constructor(
    private actions$: Actions,
    private adminService: AdminService,
    private registrationService: RegistrationService
  ) { }

  loadUnconfirmedPortalRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      map((action: RouterNavigatedAction) => action.payload.routerState),
      filter(routerState => {
        return routerState.url.startsWith('/admin-dashboard');
      }),
      map(routerState => {
        return FetchPortalRequests();
      })
    )
  );

    fetchPortalRequests$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FetchPortalRequests),
            switchMap(() =>
                forkJoin(
                  {
                    portalRequests: this.adminService.getUnconfirmedPortalRequests$(),
                    referralRequests: this.adminService.getReferralPortalRequests$()
                  }
                ).pipe(
                    map(({ portalRequests, referralRequests }) => {
                      portalRequests.data = portalRequests.data.concat(referralRequests.data);
                      return FetchPortalRequestsSuccess({portalRequests})
                    })
                )
            )
        )
    );

  fetchAllSystemUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FetchAllSystemUsers),
      switchMap((props) =>
        this.adminService.getAllSystemUsers$(props.userPageRequest).pipe(
          map((users: WebResponse<PaginatedResult<UserStatusInfo>>) =>{
            return FetchAllSystemUsersSuccess({ users });
          } )
        )
      )
    )
  );

  fetchCaseContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FetchCaseContacts),
      switchMap(() =>
        this.registrationService.getCaseContactList().pipe(
          map((caseContacts: CaseContact[]) => FetchCaseContactsSuccess({ caseContacts }))
        )
      )
    )
  );

  fetchClinics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FetchClinics),
      switchMap(() =>
        this.registrationService.getClinicList().pipe(
          map((clinics: Clinic[]) => FetchClinicsSuccess({ clinics }))
        )
      )
    )
  );

  fetchProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FetchProviders),
      switchMap(() =>
        this.registrationService.getProviderList().pipe(
          map((providers: Provider[]) => FetchProvidersSuccess({ providers }))
        )
      )
    )
  );

  searchCaseContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseContactSearchTermChanged),
      switchMap((props) =>
        this.registrationService.getCaseContactList(props.searchTerm).pipe(
          map((caseContacts: CaseContact[]) => FetchCaseContactsSuccess({ caseContacts }))
        )
      )
    )
  );
}
